import { memo } from "react";
import { styled } from "styled-components";

import { ImageBackground, PlayIconCircled, SourceLabel } from "common/components";
import { lineTruncationStyle } from "common/styles";
import { useDispatch } from "common/utilities/react-redux";
import { useGo } from "common/utilities/useGo";
import { StyledContent, StyledTitle } from "domains/knowledgeFeed/components/shared";
import { KnowledgeDetailButton } from "domains/publication/components/KnowledgeDetailButton";
import { useMedia } from "domains/viewport/useMedia";
import { setVisualMediaPlayer } from "domains/visualMediaPlayer/actions";
import type { MediaType } from "domains/visualMediaPlayer/types";
import type { Source } from "entities/knowledge/types";

const StyledSmallPlayIcon = styled(PlayIconCircled)`
  transition: transform 0.3s;
  width: 44px;
  height: 44px;
  position: absolute;
  top: 23px; // 90 - 44 = 46 / 2 = 23
  left: 38px; // 120 - 44 = 76 / 2 = 38
  margin: auto;
`;

const StyledListLink = styled.div`
  display: flex;
  flex-direction: column;

  &:hover ${StyledSmallPlayIcon} {
    transform: scale(1.2);
  }
`;

const StyledSummary = styled.div`
  font-weight: normal;
  font-size: 0.8rem;
  line-height: 1.1rem;
  color: ${({ theme }) => theme.colors.textFade};
  margin-bottom: 5px;
  ${lineTruncationStyle(3)}
`;

const StyledRow = styled.div`
  display: flex;
`;

const StyledOuterRow = styled(StyledRow)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledImageContainer = styled.div`
  position: relative;
  width: 120px;
  min-width: 120px;
  height: 90px;
  border-radius: 5px;
  margin-right: 1rem;
`;

const StyledImage = styled(ImageBackground)`
  border-radius: 4px;
`;

type SmallVideoItemProps = {
  isSelected?: boolean;
  id: string;
  title: string;
  summary?: string;
  imageUrl?: string;
  mediaType: MediaType;
  videoUrl?: string;
  source?: Source;
  timestamp: number;
  link: string;
  onClick?: () => void;
};

export const SmallVideoItem = memo(
  ({ id, title, summary, imageUrl, mediaType, videoUrl, source, timestamp, link }: SmallVideoItemProps) => {
    const dispatch = useDispatch();
    const go = useGo();
    const isSmallScreen = useMedia({ small: true, medium: true }, false);

    const goToVideoPageOrOpenVideoPlayer = () => {
      if (isSmallScreen) {
        go(link);
        return;
      }

      dispatch(setVisualMediaPlayer({ id, mediaType, name: title, url: videoUrl }));
    };

    return (
      <StyledListLink onClick={goToVideoPageOrOpenVideoPlayer}>
        <StyledOuterRow>
          <StyledRow>
            <StyledImageContainer>
              <StyledImage src={imageUrl} />
              <StyledSmallPlayIcon />
            </StyledImageContainer>
            <StyledContent>
              <StyledTitle>{title}</StyledTitle>
              {summary && <StyledSummary>{summary}</StyledSummary>}
              {source && <SourceLabel date={timestamp} hideImage source={source} type="small" />}
            </StyledContent>
          </StyledRow>
          <KnowledgeDetailButton link={link} onClick={(event) => event.stopPropagation()} title={title} />
        </StyledOuterRow>
      </StyledListLink>
    );
  },
);

SmallVideoItem.displayName = "SmallVideoItem";
